import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { members as mockMembers } from "@/mocks/members";
import { normalizeWhatsapp } from "@/lib/whatsapp";
import MemberPhotoPlaceholder from "@/components/base/MemberPhotoPlaceholder";
import { normalizePhotoPosition, PHOTO_POSITION_CLASS } from "@/lib/avatar";

interface Member {
  id: string;
  name: string;
  profession: string;
  description: string;
  photo: string;
  photo_position?: string;
  whatsapp: string | null;
  email: string;
  instagram?: string;
}

function getCardsPerView() {
  if (typeof window === "undefined") return 4;
  const w = window.innerWidth;
  if (w < 640) return 1;
  if (w < 1024) return 2;
  return 4;
}

const SWIPE_THRESHOLD = 50;
const AUTOPLAY_INTERVAL = 4500;

export default function CommunitySection() {
  const [members, setMembers] = useState<Member[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(getCardsPerView());
  const [noTransition, setNoTransition] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPausedRef = useRef(false);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Build infinite carousel array: [last N] + [originals] + [first N]
  const carouselMembers = (() => {
    if (members.length <= cardsPerView) return members;
    const head = members.slice(-cardsPerView);
    const tail = members.slice(0, cardsPerView);
    return [...head, ...members, ...tail];
  })();

  const totalSlides = members.length;

  // Responsive cardsPerView
  useEffect(() => {
    const handleResize = () => {
      const newCardsPerView = getCardsPerView();
      setCardsPerView((prev) => {
        if (prev !== newCardsPerView) {
          setCurrentIndex(newCardsPerView);
        }
        return newCardsPerView;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    async function load() {
      const { data: { session } } = await supabase.auth.getSession();
      const fonte = session ? "members_directory" : "members_public";
      let { data, error } = await supabase
        .from(fonte)
        .select("*")
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (fonte === "members_directory" && (error || !data || data.length === 0)) {
        ({ data, error } = await supabase
          .from("members_public")
          .select("*")
          .eq("status", "active")
          .order("created_at", { ascending: false }));
      }

      if (!error && data && data.length > 0) {
        const dbMembers: Member[] = data.map((m) => ({
          id: String(m.id),
          name: m.full_name,
          profession: m.profession || "",
          description: m.description || "",
          photo: m.photo || "",
          photo_position: m.photo_position || "top",
          whatsapp: normalizeWhatsapp(m.whatsapp, m.country_code),
          email: m.email || "",
          instagram: m.instagram || "",
        }));
        // Quem tem foto aparece primeiro; dentro disso, quem escreveu a descrição (10/14: muitos cartões vazios no começo)
        const peso = (m: Member) => (m.photo ? 2 : 0) + (m.description ? 1 : 0);
        dbMembers.sort((a, b) => peso(b) - peso(a));
        setMembers(dbMembers);
        setCurrentIndex(getCardsPerView());
      } else {
        const normalizedMocks = mockMembers.map((m) => ({
          ...m,
          whatsapp: normalizeWhatsapp(m.whatsapp),
        }));
        setMembers(normalizedMocks);
        setCurrentIndex(getCardsPerView());
      }
    }
    load();
  }, []);

  // Helper: reposition without transition after animation completes
  const reposition = useCallback(
    (newIndex: number) => {
      const timer = setTimeout(() => {
        setNoTransition(true);
        setCurrentIndex(newIndex);
        const clearTimer = setTimeout(() => {
          setNoTransition(false);
        }, 50);
        return () => clearTimeout(clearTimer);
      }, 600);
      return () => clearTimeout(timer);
    },
    []
  );

  const goNext = useCallback(() => {
    if (members.length <= cardsPerView) return;
    setCurrentIndex((prev) => {
      const next = prev + 1;
      // If we hit the tail clones (after last real slide), jump back to first real
      if (next >= totalSlides + cardsPerView) {
        reposition(cardsPerView);
        return next;
      }
      return next;
    });
  }, [members.length, cardsPerView, totalSlides, reposition]);

  const goPrev = useCallback(() => {
    if (members.length <= cardsPerView) return;
    setCurrentIndex((prev) => {
      const next = prev - 1;
      // If we hit the head clones (before first real slide), jump to last real
      if (next < cardsPerView) {
        reposition(totalSlides + cardsPerView - 1);
        return next;
      }
      return next;
    });
  }, [members.length, cardsPerView, totalSlides, reposition]);

  // Autoplay: advance every AUTOPLAY_INTERVAL ms, pause on hover/touch
  useEffect(() => {
    if (members.length <= cardsPerView) return;

    const startAutoplay = () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
      autoplayRef.current = setInterval(() => {
        if (!isPausedRef.current) {
          goNext();
        }
      }, AUTOPLAY_INTERVAL);
    };

    startAutoplay();

    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, [members.length, cardsPerView, goNext]);

  const pauseAutoplay = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const resumeAutoplay = useCallback(() => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false;
    }, 800);
  }, []);

  // Touch / Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    pauseAutoplay();
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff > 0) {
        goNext();
      } else {
        goPrev();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
    resumeAutoplay();
  };

  // Real slide index for dots (modulo)
  const realSlideIndex =
    members.length <= cardsPerView
      ? 0
      : ((currentIndex - cardsPerView) % totalSlides + totalSlides) % totalSlides;

  const showNav = members.length > cardsPerView;

  return (
    <section id="comunidade" className="w-full bg-beige py-16 md:py-24 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      <div className="w-full px-6 md:px-10 lg:px-16 max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-12 md:mb-16">
          <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
            Nossa Comunidade
          </p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-brown">
            Mulheres que inspiram e conectam
          </h2>
          <p className="font-body text-brown/70 mt-4 max-w-xl mx-auto text-base leading-relaxed">
            Conheça as integrantes da nossa comunidade. Cada mulher aqui traz
            uma luz única e um propósito especial.
          </p>
        </div>

        {/* Carousel */}
        <div
          className="relative"
          onMouseEnter={pauseAutoplay}
          onMouseLeave={resumeAutoplay}
        >
          {/* Navigation Arrows */}
          {showNav && (
            <>
              <button
                onClick={goPrev}
                className="absolute left-3 md:left-0 top-1/2 -translate-y-1/2 md:-translate-x-5 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white text-brown hover:bg-primary hover:text-white transition-all duration-300 shadow-sm cursor-pointer"
                aria-label="Anterior"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                onClick={goNext}
                className="absolute right-3 md:right-0 top-1/2 -translate-y-1/2 md:translate-x-5 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white text-brown hover:bg-primary hover:text-white transition-all duration-300 shadow-sm cursor-pointer"
                aria-label="Próximo"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </>
          )}

          {/* Cards container with overflow hidden + touch events */}
          <div
            className="overflow-hidden mx-10 md:mx-12 select-none"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              ref={trackRef}
              className={`flex ${noTransition ? "" : "transition-transform duration-500 ease-in-out"}`}
              style={{
                transform: `translateX(-${currentIndex * (100 / cardsPerView)}%)`,
              }}
            >
              {carouselMembers.map((member, idx) => (
                <div
                  key={`${member.id}-${idx}`}
                  className="shrink-0 px-2 md:px-3"
                  style={{ width: `${100 / cardsPerView}%` }}
                >
                  <div className="group bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-md h-full flex flex-col">
                    <div className="aspect-square overflow-hidden">
                      <Link to={`/perfil/${member.id}`} className="block w-full h-full">
                        {member.photo ? (
                          <img
                            src={member.photo}
                            alt={member.name}
                            className={`w-full h-full object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(member.photo_position)]} transition-transform duration-700 group-hover:scale-105`}
                            draggable={false}
                          />
                        ) : (
                          <MemberPhotoPlaceholder size="xl" rounded="none" name={member.name} />
                        )}
                      </Link>
                    </div>
                    <div className="p-4 md:p-5 flex-1 flex flex-col">
                      <Link
                        to={`/perfil/${member.id}`}
                        className="block font-display text-base md:text-lg text-brown mb-1 hover:text-primary transition-colors cursor-pointer"
                      >
                        {member.name}
                      </Link>
                      <p className="font-body text-xs text-primary tracking-wide uppercase mb-3">
                        {member.profession}
                      </p>
                      {member.description && (
                        <p className="font-body text-sm text-brown/70 leading-relaxed mb-4 line-clamp-3">
                          {member.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-auto">
                        {member.whatsapp && (
                          <a
                            href={`https://wa.me/${member.whatsapp}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-beige text-brown hover:bg-primary hover:text-white transition-colors duration-300 cursor-pointer"
                            title="WhatsApp"
                          >
                            <i className="ri-whatsapp-line text-sm" />
                          </a>
                        )}
                        {member.email && (
                          <a
                            href={`mailto:${member.email}`}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-beige text-brown hover:bg-primary hover:text-white transition-colors duration-300 cursor-pointer"
                            title="Email"
                          >
                            <i className="ri-mail-line text-sm" />
                          </a>
                        )}
                        {member.instagram && (
                          <a
                            href={`https://instagram.com/${member.instagram.replace("@", "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-beige text-brown hover:bg-primary hover:text-white transition-colors duration-300 cursor-pointer"
                            title="Instagram"
                          >
                            <i className="ri-instagram-line text-sm" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Slide indicators */}
          {showNav && (
            <div className="flex items-center justify-center gap-2 mt-8">
              {Array.from({ length: totalSlides }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setCurrentIndex(i + cardsPerView);
                  }}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    i === realSlideIndex
                      ? "w-5 h-2 bg-primary"
                      : "w-2 h-2 bg-nude hover:bg-primary/40"
                  }`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/comunidade"
            className="inline-block font-body text-sm md:text-base px-8 py-3.5 rounded-full border-2 border-primary text-primary tracking-wide uppercase transition-all duration-300 hover:bg-primary hover:text-white whitespace-nowrap"
          >
            Ver comunidade completa
          </Link>
        </div>
      </div>
    </section>
  );
}